import { useMemo, useState } from 'react';
import { NJButton, NJFormItem, NJSelect } from '@engie-group/fluid-design-system-react';
import { Size2MixInfoGrid, Size2MixNetworkMap, PUC_EDM_GEOMETRY, type NetworkMapColorBy } from '@shared/size2mix-shell';
import type { OptimisationDetail } from './types';

const PIPE_METRICS: Array<{ id: NetworkMapColorBy; label: string }> = [
    { id: 'pressure', label: 'Pression moyenne' },
    { id: 'velocity', label: 'Vitesse' },
    { id: 'diameter', label: 'Diamètre nominal' },
    { id: 'none', label: 'Aucune' },
];

export default function MapTab({ detail }: { detail: OptimisationDetail }) {
    const [colorBy, setColorBy] = useState<NetworkMapColorBy>('pressure');
    const [nodes, setNodes] = useState<'substations' | 'none'>('substations');
    const [search, setSearch] = useState('');
    const [step, setStep] = useState(0);
    const [selected, setSelected] = useState<{ id: string; kind: 'pipe' | 'substation' | 'departure' } | null>(null);

    const selection = useMemo(() => {
        if (!selected) return null;
        if (selected.kind === 'pipe') {
            const p = PUC_EDM_GEOMETRY.pipes.find((x) => x.id === selected.id);
            return p && { title: `Canalisation ${p.id}`, rows: [
                { label: 'DN', value: `${p.dn} mm` },
                { label: 'Longueur', value: `${p.len} m` },
                { label: 'Pression moyenne', value: `${p.p} bar` },
                { label: 'Vitesse', value: `${p.v} m/s` },
            ] };
        }
        if (selected.kind === 'substation') {
            const s = PUC_EDM_GEOMETRY.substations.find((x) => x.id === selected.id);
            return s && { title: s.id, rows: [
                { label: 'Puissance nominale', value: `${s.kw} kW` },
                { label: 'Puissance livrée', value: `${s.mw} MW` },
            ] };
        }
        return { title: `Départ ${selected.id}`, rows: [] };
    }, [selected]);

    const timestamps = detail.results.timestamps;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: 'calc(100vh - 330px)', minHeight: 520 }}>
            <p role="note" style={{ margin: 0, fontSize: 14 }}>Carte illustrative PUC EDM historique, et non les résultats géographiques de Hiver-été. Géométrie test PUC non capturée ; vue non certifiée.</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ width: 200 }}>
                    <NJSelect.Root id="ref-map-nodes" label="Nœuds" labelKind="static" listNavigationLabel="Flèches + Entrée" buttonDefaultValueLabel="Nœuds" initialValue={nodes} onChange={(v) => setNodes(v as typeof nodes)}>
                        <NJSelect.Item value="substations">Sous-stations</NJSelect.Item>
                        <NJSelect.Item value="none">Masquer</NJSelect.Item>
                    </NJSelect.Root>
                </div>
                <div style={{ width: 220 }}>
                    <NJSelect.Root id="ref-map-pipes" label="Canalisations" labelKind="static" listNavigationLabel="Flèches + Entrée" buttonDefaultValueLabel="Canalisations" initialValue={colorBy} onChange={(v) => setColorBy(v as NetworkMapColorBy)}>
                        {PIPE_METRICS.map((m) => (
                            <NJSelect.Item key={m.id} value={m.id}>
                                {m.label}
                            </NJSelect.Item>
                        ))}
                    </NJSelect.Root>
                </div>
                <div style={{ width: 280 }}>
                    <NJFormItem
                        id="ref-map-search"
                        label="Rechercher une entité"
                        labelKind="static"
                        iconName="search"
                        value={search}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    />
                </div>
                <span style={{ flex: 1 }} />
                <NJButton label="Puissance max." emphasis="minimal" scale="sm" icon="bolt" />
                <NJButton label="Puissance demandée" emphasis="minimal" scale="sm" icon="show_chart" />
            </div>

            <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
                <div style={{ flex: 1, border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                    <Size2MixNetworkMap
                        colorBy={colorBy}
                        showSubstations={nodes === 'substations'}
                        highlight={search}
                        selectedId={selected?.id}
                        onSelect={(id, kind) => setSelected(id && kind ? { id, kind } : null)}
                    />
                </div>
                {selection && (
                    <aside
                        style={{
                            width: 320,
                            padding: 16,
                            background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                            border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                        }}
                    >
                        <h3 style={{ marginTop: 0, fontSize: 16 }}>{selection.title}</h3>
                        <Size2MixInfoGrid labelWidth={140} rows={selection.rows} />
                    </aside>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{timestamps[step]} (UTC)</span>
                <input
                    type="range"
                    min={0}
                    max={timestamps.length - 1}
                    value={step}
                    onChange={(e) => setStep(Number(e.target.value))}
                    aria-label="Pas de temps"
                    style={{ flex: 1, accentColor: 'var(--nj-core-color-blue-600)' }}
                />
            </div>
        </div>
    );
}
